import React from "react";
import { MessageCircleReply, SmilePlus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "ui/Button";
import MediaCarousel from "ui/MediaCarousel";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "ui/DropdownMenu";
import ActorAvatar from "components/Actors/ActorAvatar";
import ActorName from "components/Actors/ActorName";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { toActorFromChatActor } from "util/actorIdentity";
import { transformHtmlContent } from "util/transformHtmlContent";
import { formatRelativeTime } from "config/dayjsConfig";
import type { ChatConversation, ChatMessage as ChatMessageType } from "types/chat";
import { useChatReactionMutation } from "hooks/queries/useChatQueries";
import { api } from "lib/api";
import { apiRoutes } from "util/apiRoutes";
import type { PollResponse } from "types/poll";
import PollCard from "components/Comments/PollCard";
import ChatContentCard from "./ChatContentCard";
import { useEntityPreview } from "hooks/useEntityPreview";
import type { SharedEntityMeta } from "types/chat";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

interface ChatMessageProps {
  conversation: ChatConversation;
  message: ChatMessageType;
  onReply?: (message: ChatMessageType) => void;
  onOpenThread?: (message: ChatMessageType) => void;
  onOpenSharedEntity?: (sharedEntity: SharedEntityMeta) => void;
}

const REACTION_OPTIONS = ["👍", "❤️", "😂", "🎉", "👀"];

const ChatMessage: React.FC<ChatMessageProps> = ({
  conversation,
  message,
  onReply,
  onOpenThread,
  onOpenSharedEntity,
}) => {
  const addReactionMutation = useChatReactionMutation(conversation.public_id, "add");
  const removeReactionMutation = useChatReactionMutation(conversation.public_id, "remove");
  const hasDeletedState = Boolean(message.deleted_at);
  const pollQuery = useQuery({
    queryKey: ["chat", "message-poll", message.id],
    queryFn: async () => {
      const response = await api.get<PollResponse>(apiRoutes.polls.message(message.id));
      return response.data;
    },
    enabled: message.has_poll,
  });

  const mediaItems = message.attachments
    .filter((attachment) => attachment.media_type === "image" || attachment.media_type === "video" || attachment.media_type === "gif")
    .map((attachment) => ({
      key: attachment.key,
      order: attachment.sort_order,
      type: attachment.media_type === "image" || attachment.media_type === "gif" ? "image" as const : "video" as const,
      signed_url: attachment.signed_url,
      url: attachment.url,
      originalName: attachment.file_name || undefined,
      extended_metadata: {
        width: attachment.width || undefined,
        height: attachment.height || undefined,
      },
    }));
  const fileItems = message.attachments.filter((attachment) => attachment.media_type === "file");
  const sharedEntityFromMessage = message.properties.shared_entity;
  const repliedToPlacement = message.properties.replied_to_placement ?? null;
  const previewUrl = !sharedEntityFromMessage
    ? message.body_rich?.urls?.find((url) => /^https?:\/\//i.test(url))
    : null;
  const entityPreviewQuery = useEntityPreview(
    previewUrl,
    Boolean(previewUrl) && !sharedEntityFromMessage
  );
  const sharedEntity = sharedEntityFromMessage || entityPreviewQuery.data || null;

  // A "Replied to {user}'s story" label renders above the rich card only
  // when this message is a story reply. The label text comes from the
  // legacy storyUi strings so it stays localized in all three locales.
  const storyReplyContextLabel: string | null = repliedToPlacement
    ? (() => {
        const ui = getStoryUiStrings(resolveStoryLocale());
        const authorUsername = repliedToPlacement.snapshot?.author_username;
        const ownerLabel =
          typeof authorUsername === "string" && authorUsername.trim().length > 0
            ? `@${authorUsername.trim()}`
            : `#${repliedToPlacement.owner_user_id}`;
        return ui.repliedToStoryLabel.replace("{owner}", ownerLabel);
      })()
    : null;
  const normalizedBody = message.body.trim();
  const shouldRenderBodyText = normalizedBody.length > 0 && (
    !sharedEntity ||
    normalizedBody.toLowerCase() !== sharedEntity.title.trim().toLowerCase()
  );

  const handleReactionClick = async (reactionKey: string, reacted: boolean): Promise<void> => {
    if (reacted) {
      await removeReactionMutation.mutateAsync({
        messagePublicId: message.public_id,
        reactionKey,
      });
      return;
    }
    await addReactionMutation.mutateAsync({
      messagePublicId: message.public_id,
      reactionKey,
    });
  };

  // In-thread message bubbles never show a story ring (it would be
  // distracting when the same actor appears in many bubbles back-to-back)
  // and the avatar always opens the actor's profile, never their story.
  // The chat conversation header still has a ringed avatar that opens
  // the story — this is intentional per the Instagram-style spec.
  const senderActor = toActorFromChatActor(message.sender);
  const senderName =
    message.sender?.display_name || message.sender?.username || "Unknown user";

  return (
    <div className="flex items-start gap-3">
      <div className="mt-1">
        <ActorAvatar
          actor={senderActor}
          size={ACTOR_AVATAR_SIZES.message}
          clickTo="profile"
          showRing={false}
        />
      </div>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2">
          {senderActor ? (
            <ActorName
              actor={senderActor}
              label={senderName}
              className="font-medium text-eoe-text dark:text-eoe-dark-text"
            />
          ) : (
            <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
              {senderName}
            </div>
          )}
          <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {formatRelativeTime(message.created_at)} ago
          </div>
          {message.edited_at ? (
            <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              edited
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover p-3">
          {hasDeletedState ? (
            <div className="text-sm italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
              Message deleted
            </div>
          ) : (
            <>
              {message.body_rich?.html ? (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  {transformHtmlContent(message.body_rich.html, {
                    replaceLinksWithPreviews: sharedEntity === null,
                  })}
                </div>
              ) : shouldRenderBodyText ? (
                <div className="whitespace-pre-wrap text-sm text-eoe-text dark:text-eoe-dark-text">
                  {message.body}
                </div>
              ) : null}

              {sharedEntity ? (
                <ChatContentCard
                  sharedEntity={sharedEntity}
                  onOpen={onOpenSharedEntity}
                  className="mt-3"
                  contextLabel={storyReplyContextLabel || undefined}
                />
              ) : null}

              {mediaItems.length > 0 ? (
                <MediaCarousel
                  mediaFiles={mediaItems}
                  className="mt-3 overflow-hidden rounded-xl"
                  showDots={mediaItems.length > 1}
                  showArrows={mediaItems.length > 1}
                />
              ) : null}

              {fileItems.length > 0 ? (
                <div className="mt-3 flex flex-col gap-2">
                  {fileItems.map((attachment) => (
                    <a
                      key={attachment.key}
                      href={attachment.signed_url || attachment.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main px-3 py-2 text-sm hover:bg-eoe-bg dark:hover:bg-eoe-dark-bg"
                    >
                      {attachment.file_name || attachment.key}
                    </a>
                  ))}
                </div>
              ) : null}

              {message.has_poll && pollQuery.data ? (
                <div className="mt-3">
                  <PollCard
                    mode="display"
                    pollData={pollQuery.data}
                    onRefresh={async () => {
                      await pollQuery.refetch();
                    }}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {message.reactions.map((reaction) => (
            <button
              key={reaction.reaction_key}
              type="button"
              onClick={() => void handleReactionClick(reaction.reaction_key, reaction.reacted)}
              className={`rounded-full border px-2 py-1 text-xs ${
                reaction.reacted
                  ? "border-soc-purple text-soc-purple"
                  : "border-eoe-border-main dark:border-eoe-dark-border-main text-eoe-text-sub dark:text-eoe-dark-text-sub"
              }`}
            >
              {reaction.reaction_key} {reaction.count}
            </button>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-full border border-eoe-border-main dark:border-eoe-dark-border-main p-1 text-eoe-text-sub dark:text-eoe-dark-text-sub"
                aria-label="Add reaction"
              >
                <SmilePlus className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {REACTION_OPTIONS.map((reaction) => (
                <DropdownMenuItem
                  key={reaction}
                  onClick={() => void handleReactionClick(reaction, false)}
                >
                  {reaction}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReply?.(message)}
          >
            <MessageCircleReply />
            Reply
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenThread?.(message)}
          >
            Thread
            {message.thread_reply_count > 0 ? ` (${message.thread_reply_count})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
